import { GlobalFilterState, analytics_date_kind } from "@/global_filter"
import { getStartEnd } from "@/global_filter/date_range_filter"
import { api_fetch } from "@/api/client"
import { formatDistributor, map, toISODateString } from "@/utils"
import { format, parseISO } from "date-fns"
import { useEffect, useMemo, useState } from "preact/hooks"
import useSWR from "swr"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import { DC_COLORS, formatAmount } from "./dc_map"

const TOP_RETAILER_DEFAULT = 10

interface SplitRetailerTotalPoint {
  retailer_name: string
  total_amount: number
}

interface SplitRetailerTrendPoint {
  retailer_name: string
  month: string
  amount: number
}

interface SplitRetailerSummaryResponse {
  by_retailer: SplitRetailerTotalPoint[]
  trend: SplitRetailerTrendPoint[]
}

async function fetchSplitRetailerSummary(params: {
  start_date: string | undefined
  end_date: string | undefined
  distributor: string | null | undefined
  date_kind: string
}): Promise<SplitRetailerSummaryResponse> {
  const res = await api_fetch<SplitRetailerSummaryResponse>("/deductions/split-retailer-summary", {
    params: {
      start_date: params.start_date,
      end_date: params.end_date,
      distributor: params.distributor ?? undefined,
      date_kind: params.date_kind,
    },
  })
  if (!res.ok) {
    throw res.error
  }
  return res.value.data
}

function formatRetailerLabel(raw: string): string {
  return formatDistributor(raw)
}

interface TrendChartRow {
  month: string
  [retailerKey: string]: string | number | undefined
}

export function SplitRetailerTrendChart() {
  const [startDate, endDate, distributor, dateKind] = GlobalFilterState.use(s => [
    s.startDate,
    s.endDate,
    s.distributor,
    s.dateKind,
  ])

  const swrKey = [
    "split-retailer-summary",
    map(startDate, toISODateString),
    map(endDate, toISODateString),
    distributor ?? "",
    dateKind,
  ] as const

  const { data, isLoading, error } = useSWR(
    swrKey,
    () =>
      fetchSplitRetailerSummary({
        start_date: map(startDate, toISODateString),
        end_date: map(endDate, toISODateString),
        distributor,
        date_kind: analytics_date_kind(dateKind),
      }),
    { revalidateOnFocus: false }
  )

  const retailerNamesOrdered = useMemo(() => {
    if (!data?.by_retailer?.length) return []
    return data.by_retailer.map(r => r.retailer_name)
  }, [data?.by_retailer])

  const [selectedRetailers, setSelectedRetailers] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState("")

  useEffect(() => {
    if (!data?.by_retailer?.length) {
      setSelectedRetailers([])
      return
    }
    const top = data.by_retailer.slice(0, TOP_RETAILER_DEFAULT).map(r => r.retailer_name)
    setSelectedRetailers(top)
  }, [data])

  const selectedSet = useMemo(() => new Set(selectedRetailers), [selectedRetailers])

  const filteredRetailersForList = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return retailerNamesOrdered
    return retailerNamesOrdered.filter(name => name.toLowerCase().includes(q))
  }, [retailerNamesOrdered, searchQuery])

  const trendChartData = useMemo<TrendChartRow[]>(() => {
    if (!data?.trend.length) return []
    const byMonth: Record<string, TrendChartRow> = {}
    for (const p of data.trend) {
      const monthKey = typeof p.month === "string" ? p.month : format(p.month as unknown as Date, "yyyy-MM-dd")
      if (!byMonth[monthKey]) {
        byMonth[monthKey] = { month: monthKey }
      }
      const row = byMonth[monthKey]
      row[p.retailer_name] = (Number(row[p.retailer_name]) || 0) + p.amount
    }
    return Object.values(byMonth).sort((a, b) => a.month.localeCompare(b.month))
  }, [data?.trend])

  const chartConfig = useMemo(() => {
    const config: Record<string, { label: string; color: string }> = {}
    retailerNamesOrdered.forEach((name: string, idx: number) => {
      config[name] = { label: formatRetailerLabel(name), color: DC_COLORS[idx % DC_COLORS.length] }
    })
    return config
  }, [retailerNamesOrdered])

  const visibleRetailerNames = useMemo(() => {
    return retailerNamesOrdered.filter((name: string) => selectedSet.has(name))
  }, [retailerNamesOrdered, selectedSet])

  function toggleRetailer(name: string) {
    setSelectedRetailers((current: string[]) =>
      current.includes(name) ? current.filter((x: string) => x !== name) : [...current, name]
    )
  }

  function selectAllRetailers() {
    setSelectedRetailers(retailerNamesOrdered)
  }

  function clearAllRetailers() {
    setSelectedRetailers([])
  }

  function handleShowAllCustomers() {
    GlobalFilterState.set(s => ({ ...s, distributor: null }))
  }

  function handleShowPastYear() {
    const range = getStartEnd("pastYear")
    GlobalFilterState.set(s => ({
      ...s,
      startDate: range.startDate,
      endDate: range.endDate,
    }))
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Deductions by retailer (splits)</h3>
        </CardHeader>
        <CardContent>
          <div className="h-40 flex items-center justify-center text-destructive text-sm">
            Could not load split retailer data.
          </div>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Deductions by retailer (splits)</h3>
        </CardHeader>
        <CardContent>
          <div className="h-48 flex items-center justify-center text-gray-400">Loading split data…</div>
        </CardContent>
      </Card>
    )
  }

  if (!data?.by_retailer.length) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Deductions by retailer (splits)</h3>
          <p className="text-sm text-gray-500">Accounting split amounts by retailer name (falls back to customer when retailer is blank).</p>
        </CardHeader>
        <CardContent>
          <div className="h-40 flex items-center justify-center text-gray-400">
            No split rows in this date range. Data appears after deductions are coded with splits.
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-gray-800">Deductions by retailer (splits)</h2>
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="text-base font-semibold">Monthly trend</h3>
              <p className="text-sm text-gray-500">
                Split-attributed deduction dollars by month. By default the chart shows the top {TOP_RETAILER_DEFAULT} retailers by
                total split amount for your filters. Search below or use Select all to add others.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <Button type="button" variant="outline" size="sm" onClick={handleShowAllCustomers}>
                Show all customers
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={handleShowPastYear}>
                Show past year
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="text-xs text-gray-600 hover:text-gray-800"
              onClick={selectAllRetailers}
            >
              Select all retailers
            </button>
            <span className="text-gray-300">|</span>
            <button type="button" className="text-xs text-gray-600 hover:text-gray-800" onClick={clearAllRetailers}>
              Clear all
            </button>
          </div>
          <div className="mb-3">
            <Input
              placeholder="Search retailers…"
              value={searchQuery}
              onInput={(e: Event) => setSearchQuery((e.target as HTMLInputElement).value)}
              className="max-w-sm"
            />
          </div>
          <div className="mb-3 max-h-40 overflow-y-auto rounded-md border border-gray-100 p-2 flex flex-wrap gap-1.5">
            {filteredRetailersForList.map((name: string) => {
              const idx = retailerNamesOrdered.indexOf(name)
              const isSelected = selectedSet.has(name)
              const color = DC_COLORS[(idx >= 0 ? idx : 0) % DC_COLORS.length]
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => toggleRetailer(name)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs transition-colors ${
                    isSelected ? "border-gray-300 bg-gray-100 text-gray-800" : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                  <span className="truncate max-w-[14rem]">{formatRetailerLabel(name)}</span>
                </button>
              )
            })}
          </div>
          <div className="h-[300px]">
            {!visibleRetailerNames.length ? (
              <div className="h-full flex items-center justify-center text-sm text-gray-500">
                Select at least one retailer to show the trend.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tickFormatter={(val: string) => {
                      try {
                        return format(parseISO(val), "MMM yy")
                      } catch {
                        return val
                      }
                    }}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    tickFormatter={formatAmount}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 11 }}
                    width={55}
                  />
                  <Tooltip
                    content={({ active, payload, label }: any) => {
                      if (!active || !payload?.length) return null
                      const sorted = [...payload].sort((a: any, b: any) => (b.value || 0) - (a.value || 0))
                      let monthLabel = label
                      try {
                        monthLabel = format(parseISO(String(label)), "MMMM yyyy")
                      } catch {
                        /* keep label */
                      }
                      return (
                        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
                          <p className="font-semibold mb-2">{monthLabel}</p>
                          {sorted.map((entry: any, idx: number) => (
                            <div key={idx} className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-1.5">
                                <div
                                  className="w-2.5 h-2.5 rounded-full shrink-0"
                                  style={{ backgroundColor: entry.color }}
                                />
                                <span className="text-gray-700">
                                  {formatRetailerLabel(String(entry.name ?? entry.dataKey ?? ""))}
                                </span>
                              </div>
                              <span className="font-medium">{formatAmount(entry.value || 0)}</span>
                            </div>
                          ))}
                        </div>
                      )
                    }}
                  />
                  <Legend
                    formatter={(value: string) => formatRetailerLabel(value)}
                    wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }}
                  />
                  {visibleRetailerNames.map((name: string, idx: number) => (
                    <Line
                      key={name}
                      type="monotone"
                      dataKey={name}
                      name={name}
                      stroke={chartConfig[name]?.color || DC_COLORS[idx % DC_COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
